import SimpleBarGraph from "@/components/Global/SimpleBarGraph/SimpleBarGraph.component";
import { AssessmentOutcomesView } from "@/enums";
import React, { useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectAssessmentOutcomesWidget, updateAssessmentOutcomesView, fetchAssessmentOutcomesWidgetData, fetchAssessmentDyslexiaRiskWidgetData } from "../store/slices/assessmentOutcomesWidgetSlice";
import './AssessmentOutcomesWidget.scss';
import useSelectedClassroom from "@/hooks/useSelectedClassroom";
import { useNavigate } from "react-router-dom";
import Dropdown from "@/components/Global/Dropdown/Dropdown";
import { DropdownType } from "@/enums";
import { selectUserData } from '@/store/slices/UserDataSlice';
import chartOutlineIcon from "@/images/icons/chart-outline.svg";
import { AsyncLoadingState } from "../enums";
import { AssessmentOutcomesWidgetModel } from "../models/AssessmentOutcomesWidget";
import WidgetDataReloadButton from "./WidgetDataReloadButton";
import TutorBarGraph from "@/components/Global/TutorBarGraph/TutorBarGraph.component";
import "@/components/Global/SimpleBarGraph/SimpleBarGraph.component.scss";

interface AssessmentOutcomesWidgetContainerProps {
  viewType?: AssessmentOutcomesView;
}

const AssessmentOutcomesWidget: React.FC<AssessmentOutcomesWidgetContainerProps> = ({
  viewType,
}) => {
  const userData = useSelector(selectUserData);
  const navigate = useNavigate();
  const { classroomId } = useSelectedClassroom();

  const handleGraphClick = (viewType: AssessmentOutcomesView) => {
    if (viewType === AssessmentOutcomesView.ASSESSMENT_CLASSIFICATION) {
      if (userData?.scoreType === 'ISIP') {
        window.location.href = `${process.env.ISTATION_SUMMARY_REPORT_URL}?classId=${userData?.classId}`;
      } else {
        navigate('/teacher/reports/type/benchmark');
      }
    }
    if (viewType === AssessmentOutcomesView.DYSLEXIA_RISK) {
      navigate('/teacher/reports/type/dyslexiarisk');
    }
    //add other routes here
  }
  const dispatch = useDispatch();
  const { view, classificationData, dyslexiaRiskData, loading, error } = useSelector(selectAssessmentOutcomesWidget);

  const classificationModel = useMemo(() => {
    return classificationData ? new AssessmentOutcomesWidgetModel(classificationData) : null;
  }, [classificationData]);

  const totalClassificationGraphData = useMemo(() => {
    return classificationModel?.graphData.reduce((acc: number, month: any) => {
      return acc + month.data.reduce((subAcc: number, item: any) => subAcc + item.value, 0);
    }, 0) ?? 0;
  }, [classificationModel]);

  // Calculate total dyslexia data count
  const totalDyslexiaGraphData = useMemo(() => {
    if (!dyslexiaRiskData?.classroomDyslexiaRiskData?.data?.aggregateData) {
      return 0;
    }
    const { highCount, lowCount, moderateCount } = dyslexiaRiskData.classroomDyslexiaRiskData.data.aggregateData;
    return (highCount ?? 0) + (lowCount ?? 0) + (moderateCount ?? 0);
  }, [dyslexiaRiskData]);

  const viewOptions = [
    { label: "Assessment Classification", value: AssessmentOutcomesView.ASSESSMENT_CLASSIFICATION },
    { label: "Dyslexia Risk", value: AssessmentOutcomesView.DYSLEXIA_RISK },
  ];

  const handleViewChange = (selectedOption: any) => {
    dispatch(updateAssessmentOutcomesView(selectedOption.value));
  };

  // Format dyslexia data to ensure it works with SimpleBarGraph
  const formattedDyslexiaData = useMemo(() => {
    if (!dyslexiaRiskData?.classroomDyslexiaRiskData?.data?.aggregateData) {
      return [];
    }

    const { highCount, lowCount, moderateCount } = dyslexiaRiskData.classroomDyslexiaRiskData.data.aggregateData;

    return [
      {
        label: "Low",
        data: [{
          color: "#6C9",
          label: lowCount.toString(),
          value: lowCount
        }]
      },
      {
        label: "Moderate",
        data: [{
          color: "#FC3",
          label: moderateCount.toString(),
          value: moderateCount
        }]
      },
      {
        label: "High",
        data: [{
          color: "#FF595E",
          label: highCount.toString(),
          value: highCount
        }]
      }
    ];
  }, [dyslexiaRiskData]);

  const handleRetry = () => {
    if (!classroomId) return;

    const currentView = viewType || view;
    if (currentView === AssessmentOutcomesView.DYSLEXIA_RISK) {
      dispatch(fetchAssessmentDyslexiaRiskWidgetData(classroomId) as any);
    } else {
      dispatch(fetchAssessmentOutcomesWidgetData(classroomId) as any);
    }
  };

  const isClickDisabled = useMemo(() => {
    const currentView = viewType || view;
    const hasError = loading === AsyncLoadingState.ERROR || error;

    let hasNoData = false;
    if (currentView === AssessmentOutcomesView.ASSESSMENT_CLASSIFICATION) {
      hasNoData = totalClassificationGraphData === 0
    } else if (currentView === AssessmentOutcomesView.DYSLEXIA_RISK) {
      hasNoData = totalDyslexiaGraphData === 0;
    }

    return hasError || hasNoData;
  }, [loading, error, viewType, view, totalClassificationGraphData, classificationData, dyslexiaRiskData]);

  const handleWidgetClick = () => {
    if (!isClickDisabled) {
      handleGraphClick(viewType || view);
    }
  };

  const contents = useCallback(() => {
    let curView = viewType || view;

    if (loading === AsyncLoadingState.ERROR || error) {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center p-4 widget-body">
          <div className="data-failed-text mb-3 font-weight-bold">Data failed to load</div>
          <WidgetDataReloadButton onReload={handleRetry} />
        </div>
      );
    }

    switch (curView) {
      case AssessmentOutcomesView.DYSLEXIA_RISK:
        if (totalDyslexiaGraphData === 0) {
          return (
            <div className="d-flex flex-column align-items-center justify-content-center p-4 no-data-container widget-body">
              <p className="no-classification-data-text">There's no dyslexia risk data available yet. Have students complete an assessment!</p>
            </div>
          );
        }
        return (
          <div className="outcomes-graph-container d-flex align-items-center justify-content-center w-100 p-3">
            <TutorBarGraph
              id={"dyslexia-report"}
              datasets={formattedDyslexiaData}
              gap={4}
              barSize={3}
              barTotal={30}
              onGraphClick={() => handleGraphClick(AssessmentOutcomesView.DYSLEXIA_RISK)}
            />
          </div>
        );
      default: // AssessmentOutcomesView.ASSESSMENT_CLASSIFICATION
        if (totalClassificationGraphData === 0) {
          return (
            <div className="d-flex flex-column align-items-center justify-content-center p-4 no-data-container widget-body">
              <p className="no-classification-data-text">There's no data available yet. Have students complete an assessment!</p>
            </div>
          );
        }
        return (
          <div className="outcomes-graph-container d-flex align-items-center justify-content-center w-100 p-3">
          <SimpleBarGraph
            id="assessment-classification"
            isVertical={true}
            datasets={classificationModel?.graphData || []}
            gap={4}
            barSize={4}
            hasSubsections={true}
            maxSize={20}
            onGraphClick={() => handleGraphClick(AssessmentOutcomesView.ASSESSMENT_CLASSIFICATION)}
            />
          </div>
        );
    }
  }, [viewType, view, classificationModel, formattedDyslexiaData, totalClassificationGraphData, loading, classroomId]);
  return (
    <div className="d-flex flex-column align-items-center assess-outcomes-widget-container">
      <div
        className="assess-outcomes-widget-body d-flex flex-column align-items-center"
        onClick={handleWidgetClick}
        style={{ cursor: isClickDisabled ? 'default' : 'pointer' }}
      >
        <div className="dropdown-container d-flex align-items-center justify-content-center p-2" onClick={(e) => e.stopPropagation()}>
        {((viewType || view) === AssessmentOutcomesView.ASSESSMENT_CLASSIFICATION ? (totalClassificationGraphData ?? 0) > 0 : dyslexiaRiskData !== null) && <img src={chartOutlineIcon} alt="chart-outline" className="chart-outline-icon" />}
          <Dropdown
            className="assessment-outcomes-options"
            type={DropdownType.PRIMARY}
            placeholder="Select View"
            isDisabled={false}
            value={viewOptions.find(option => option.value === (viewType || view))}
            options={viewOptions}
            onChange={handleViewChange}
            aria-label="Assessment Outcomes View Selector"
          />
        </div>
        <div className="d-flex align-items-center justify-content-center w-100">
          {contents()}
        </div>
      </div>
    </div>
  );
};

export default AssessmentOutcomesWidget;
